import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@kit/supabase/database';
import type { UntypedTableClient } from '@kit/supabase/types';

import type { ArticleService } from '../article.service';
import { ingestExternalImage } from '../shop-cover-ingest.service';
import { ShopifyAdapter } from './shopify-adapter';
import type { CatalogAdapter, NormalizedProduct, Result } from './types';

interface OrgRow {
  catalog_sync_enabled: boolean;
  external_store_platform: string | null;
  external_store_url: string | null;
  external_store_handle: string | null;
  shop_id: string | null;
}

interface ExistingRow {
  id: string;
  external_product_id: string | null;
}

export interface CatalogSyncResult {
  seen: number;
  upserted: number;
  archived: number;
  /** Empty string when no sync run was created (e.g. sync disabled no-op). */
  runId: string;
}

/**
 * Syncs a brand's external catalog into its Ballee shop as `external_sync`
 * articles (BAL-449). Redirect-out only — buyers check out on the brand's own
 * store, so this never touches on-Ballee checkout.
 *
 * Invoked with a service-role client (the nightly runner) so it can read the
 * org's store linkage, write the sync-run observability rows, and upsert
 * articles across shops. Result pattern throughout — no exception escapes.
 */
export class CatalogSyncService {
  constructor(
    private client: SupabaseClient<Database>,
    private articleService: ArticleService,
  ) {}

  async syncOrganization(orgId: string): Promise<Result<CatalogSyncResult>> {
    const db = this.client as unknown as UntypedTableClient;

    // 1. Load the org's store linkage + opt-in gate.
    const { data: orgData, error: orgErr } = await db
      .from('organizations')
      .select(
        'catalog_sync_enabled, external_store_platform, external_store_url, external_store_handle, shop_id',
      )
      .eq('id', orgId)
      .single();

    const org = orgData as OrgRow | null;

    if (orgErr || !org) {
      return {
        success: false,
        error: new Error(
          `Organization ${orgId} not found: ${orgErr?.message ?? 'no row'}`,
        ),
      };
    }

    if (!org.catalog_sync_enabled) {
      // Opt-in gate closed — no-op, no sync run created.
      return {
        success: true,
        data: { seen: 0, upserted: 0, archived: 0, runId: '' },
      };
    }

    if (!org.shop_id) {
      return {
        success: false,
        error: new Error(`Organization ${orgId} has no shop_id — cannot sync`),
      };
    }
    if (!org.external_store_url) {
      return {
        success: false,
        error: new Error(
          `Organization ${orgId} has catalog_sync_enabled but no external_store_url`,
        ),
      };
    }

    const shopId = org.shop_id;
    const platform = org.external_store_platform;

    // 2. Open a sync-run observability row.
    const { data: runData, error: runErr } = await db
      .from('shop_catalog_sync_runs')
      .insert({
        organization_id: orgId,
        shop_id: shopId,
        platform,
        status: 'running',
      })
      .select('id')
      .single();

    const runId = (runData as { id: string } | null)?.id;
    if (runErr || !runId) {
      return {
        success: false,
        error: new Error(
          `Failed to open sync run for org ${orgId}: ${runErr?.message ?? 'no row'}`,
        ),
      };
    }

    try {
      // 3. Select the adapter for the platform.
      const adapter = this.adapterFor(platform);
      if (!adapter) {
        return await this.failRun(
          db,
          runId,
          `Unsupported store platform: ${platform ?? 'null'}`,
        );
      }

      // 4. Fetch the external catalog.
      const fetched = await adapter.fetchProducts({
        url: org.external_store_url,
        handle: org.external_store_handle,
      });
      if (!fetched.success) {
        return await this.failRun(db, runId, fetched.error.message);
      }
      const products = fetched.data;

      // 5. Pre-fetch existing external rows in ONE query (no per-product read).
      const { data: existingData, error: existingErr } = await db
        .from('shop_articles')
        .select('id, external_product_id')
        .eq('shop_id', shopId)
        .eq('source', 'external_sync');

      if (existingErr) {
        return await this.failRun(db, runId, existingErr.message);
      }

      const existingMap = new Map<string, string>();
      for (const row of (existingData ?? []) as ExistingRow[]) {
        if (row.external_product_id) {
          existingMap.set(row.external_product_id, row.id);
        }
      }

      // 6. Upsert each product.
      const seenIds = new Set<string>();
      let upserted = 0;

      for (const product of products) {
        seenIds.add(product.externalId);

        // Best-effort image ingest — a failed image never blocks the product.
        let featuredImageId: string | null = null;
        let featuredImageUrl: string | null = null;
        if (product.imageUrl) {
          const ingest = await ingestExternalImage(
            this.client,
            product.imageUrl,
            { ownerType: 'organization', ownerId: shopId },
          );
          if (ingest.success) {
            featuredImageId = ingest.data.documentId;
            featuredImageUrl = ingest.data.publicUrl;
          } else {
            console.warn(
              `[catalog-sync] image ingest failed for ${product.externalId}: ${ingest.error.message}`,
            );
          }
        }

        const { priceEur, priceUsd } = this.splitPrice(product);

        const upsert = await this.articleService.upsertExternalArticle(
          {
            shopId,
            externalSource: 'shopify',
            externalProductId: product.externalId,
            externalHandle: product.handle,
            title: { en: product.title },
            description: product.description
              ? { en: product.description }
              : null,
            priceEur,
            priceUsd,
            externalUrl: product.productUrl,
            featuredImageId,
            featuredImageUrl,
          },
          existingMap.get(product.externalId) ?? null,
        );

        if (upsert.success) {
          upserted += 1;
        } else {
          console.warn(
            `[catalog-sync] upsert failed for ${product.externalId}: ${upsert.error.message}`,
          );
        }
      }

      // 7. Soft-archive drift: existing rows no longer in the feed.
      const staleIds: string[] = [];
      for (const [externalProductId, id] of existingMap.entries()) {
        if (!seenIds.has(externalProductId)) {
          staleIds.push(id);
        }
      }
      if (staleIds.length > 0) {
        const { error: archiveErr } = await db
          .from('shop_articles')
          .update({ is_published: false })
          .in('id', staleIds);
        if (archiveErr) {
          return await this.failRun(db, runId, archiveErr.message);
        }
      }

      // 8. Close the run + stamp the org's last-synced time.
      const nowIso = new Date().toISOString();
      await db
        .from('shop_catalog_sync_runs')
        .update({
          status: 'ok',
          products_seen: products.length,
          products_upserted: upserted,
          products_archived: staleIds.length,
          finished_at: nowIso,
        })
        .eq('id', runId);

      await db
        .from('organizations')
        .update({ catalog_last_synced_at: nowIso })
        .eq('id', orgId);

      return {
        success: true,
        data: {
          seen: products.length,
          upserted,
          archived: staleIds.length,
          runId,
        },
      };
    } catch (err) {
      // 9. Any thrown step fails the run.
      const message =
        err instanceof Error ? err.message : 'Catalog sync failed';
      return await this.failRun(db, runId, message);
    }
  }

  private adapterFor(platform: string | null): CatalogAdapter | null {
    if (platform === 'shopify') return new ShopifyAdapter();
    return null;
  }

  /**
   * Shopify's public feed omits currency, so we assume EUR unless the source
   * explicitly says USD. Null/EUR → price_eur; USD → price_usd; any other
   * currency → both null (unpriced) rather than mislabel it.
   */
  private splitPrice(product: NormalizedProduct): {
    priceEur: number | null;
    priceUsd: number | null;
  } {
    const amount = product.priceAmount;
    if (product.currency === 'USD') {
      return { priceEur: null, priceUsd: amount };
    }
    if (product.currency === null || product.currency === 'EUR') {
      return { priceEur: amount, priceUsd: null };
    }
    return { priceEur: null, priceUsd: null };
  }

  private async failRun(
    db: UntypedTableClient,
    runId: string,
    message: string,
  ): Promise<Result<CatalogSyncResult>> {
    await db
      .from('shop_catalog_sync_runs')
      .update({
        status: 'failed',
        error: message,
        finished_at: new Date().toISOString(),
      })
      .eq('id', runId);
    return { success: false, error: new Error(message) };
  }
}
